import { getApiData, postFormData } from "../utils/APIHelper";

const SCOPE_UPDATE_ENDPOINTS = {
    GET_SCOPE_UPDATES: (projectId, page, perPage) =>
        `api/scope/updates?project_id=${projectId}&page=${page}&per_page=${perPage}`,
    GET_SCOPE_UPDATE_DETAILS: (updateId) => `api/scope/updates/${updateId}`,
    CREATE_SCOPE_UPDATE: (scopeId, bidId) => `api/scope/${scopeId}/bid/${bidId}/updates`,
    UPDATE_SCOPE_UPDATE: (updateId) => `api/scope/updates/${updateId}`,
    GET_PROJECT_SCOPES: (projectId) => `api/project/${projectId}/scopes?scope_filter=CONTRACTED`,
    GET_ACTIVITIES_BY_SCOPE: (projectId, scopeId) =>
        `api/project/${projectId}/activities?scope_id=${scopeId}`,
    GET_UPDATES_FILES: (updateId) =>
        `api/project/updates/files?ref_id=${updateId}&ref_type=SCOPE_UPDATE`,
    UPLOAD_UPDATES_FILE: `api/project/updates/files`,
    DELETE_UPDATES_FILE: (fileId) => `api/project/updates/files/${fileId}`,
};

/**
 * List scope updates for a project, paginated & grouped by date.
 */
export const getScopeUpdates = async (projectId, { page = 1, perPage = 20 } = {}) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.GET_SCOPE_UPDATES(projectId, page, perPage),
            "GET",
            {}
        );
    } catch (error) {
        console.error("Error fetching scope updates:", error);
        throw error;
    }
};

/**
 * Single scope update by id (used to hydrate edit/view — returns data[0] shape).
 */
export const getScopeUpdateDetails = async (updateId) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.GET_SCOPE_UPDATE_DETAILS(updateId),
            "GET",
            {}
        );
    } catch (error) {
        console.error("Error fetching scope update details:", error);
        throw error;
    }
};

/**
 * Create a new scope update (phase 1 of create: POST multipart).
 * updateData = { name, message, date, data: [], files: { file_1, file_2, ... } }
 */
export const createScopeUpdate = async (scopeId, bidId, updateData) => {
    try {
        const formData = new FormData();
        formData.append("name", updateData.name);
        formData.append("message", updateData.message);
        formData.append("date", updateData.date);
        formData.append("data", JSON.stringify(updateData.data || []));

        Object.keys(updateData.files || {}).forEach((key) => {
            if (updateData.files[key]) {
                formData.append(key, updateData.files[key]);
            }
        });

        return await postFormData(
            SCOPE_UPDATE_ENDPOINTS.CREATE_SCOPE_UPDATE(scopeId, bidId),
            formData
        );
    } catch (error) {
        console.error("Error creating scope update:", error);
        throw error;
    }
};

/**
 * Update scope update (PUT, JSON body with sanitized milestones in data[]).
 */
export const updateScopeUpdate = async (updateId, updateData) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.UPDATE_SCOPE_UPDATE(updateId),
            "PUT",
            updateData,
            { "Content-Type": "application/json" }
        );
    } catch (error) {
        console.error("Error updating scope update:", error);
        throw error;
    }
};

/**
 * Contracted scopes for a project (returns { scopes: [...] }). Filter to bids.status in ongoing|in_warranty at call site.
 */
export const getProjectScopesForUpdate = async (projectId) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.GET_PROJECT_SCOPES(projectId),
            "GET",
            {}
        );
    } catch (error) {
        console.error("Error fetching project scopes:", error);
        throw error;
    }
};

/**
 * Activities for a scope (returns { data: [{ scope_id, activities: [...] }] }).
 */
export const getActivitiesByScope = async (projectId, scopeId) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.GET_ACTIVITIES_BY_SCOPE(projectId, scopeId),
            "GET",
            {}
        );
    } catch (error) {
        console.error("Error fetching activities by scope:", error);
        throw error;
    }
};

/**
 * List files attached to a scope update.
 */
export const getUpdatesFiles = async (updateId) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.GET_UPDATES_FILES(updateId),
            "GET",
            {}
        );
    } catch (error) {
        console.error("Error fetching updates files:", error);
        throw error;
    }
};

/**
 * Upload a single file to a scope update with optional meta_data JSON.
 * fileObj: File, metaData: object | string, refId: string
 */
export const uploadUpdatesFile = async ({ refId, file, metaData }) => {
    try {
        const fd = new FormData();
        fd.append("ref_id", String(refId));
        fd.append("ref_type", "SCOPE_UPDATE");
        fd.append("type", "");
        fd.append("file_1", file);
        fd.append(
            "meta_data",
            typeof metaData === "string"
                ? metaData
                : JSON.stringify(metaData || { room: null, milestones: [] })
        );
        fd.append("tag", "");
        return await postFormData(SCOPE_UPDATE_ENDPOINTS.UPLOAD_UPDATES_FILE, fd);
    } catch (error) {
        console.error("Error uploading updates file:", error);
        throw error;
    }
};

/**
 * Delete a file attached to a scope update.
 */
export const deleteUpdatesFile = async (fileId) => {
    try {
        return await getApiData(
            SCOPE_UPDATE_ENDPOINTS.DELETE_UPDATES_FILE(fileId),
            "DELETE",
            {}
        );
    } catch (error) {
        console.error("Error deleting updates file:", error);
        throw error;
    }
};
